import React, { useRef, useEffect, useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, GraduationCap, Loader2, Lock, UnfoldVertical, Move, CalendarDays, CalendarArrowUp, CalendarArrowDown, Tag, User, Users } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { FileText } from 'lucide-react';
import moment from 'moment-timezone';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@radix-ui/react-separator';

interface CalendarViewProps {
  events: any[];
  currentView: string;
  timezone: string;
  browserTimezone: string;
  currentNow: Date | null;
  showWeekends: boolean;
  onEventDrop: (eventId: string, newStart: Date | null, newEnd: Date | null) => Promise<void>;
  onEventResize: (eventId: string, newStart: Date | null, newEnd: Date | null) => Promise<void>;
  onDateClick: (arg: any) => void;
  onEventClick: (info: any) => void;
  onSelect: (arg: any) => void;
  firstDay?: number;
  slotMinTime?: string;
  slotMaxTime?: string;
  eventTextColor?: string; 
  onLoadMoreDates?: () => void; // Charge le mois suivant pour les vues longues
  canLoadMore?: boolean;
  calendarRef?: React.RefObject<FullCalendar>;
  isLoadingEvents?: boolean;
  onDatesSet?: (dateInfo: any) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  currentView,
  timezone,
  browserTimezone,
  currentNow,
  showWeekends,
  onEventDrop,
  onEventResize,
  onDateClick,
  onEventClick,
  onSelect,
  firstDay,
  slotMinTime,
  slotMaxTime,
  eventTextColor,
  calendarRef, 
  onLoadMoreDates,
  canLoadMore,
  isLoadingEvents = false,
  onDatesSet
}) => {
  const localCalendarRef = useRef<FullCalendar>(null);
  const isMobile = useIsMobile();
  const { locale: contextLocale, t } = useLanguage();
  const [tooltipData, setTooltipData] = useState<any>(null);
  const { toast } = useToast();  
  const { user, token } = useAuth();
  const [showFullWeek, setShowFullWeek] = useState(false);
  const [isCurrentWeek, setIsCurrentWeek] = useState(false);

  const handleShowUpcomingYear = () => {
    const calendarApi = (calendarRef || localCalendarRef).current?.getApi();
    if (!calendarApi) return;
    calendarApi.changeView('listYear');
  };

  const handleReturnToListWeek = () => {
    const calendarApi = (calendarRef || localCalendarRef).current?.getApi();
    if (!calendarApi) return;
    calendarApi.changeView('listWeek');
  };

  const [hourFormat, setHourFormat] = useState(() => {
    return localStorage.getItem('hourFormat') || (contextLocale === 'en' ? '12' : '24');
  });

  useEffect(() => {
    const handleHourFormatChange = () => {
      setHourFormat(localStorage.getItem('hourFormat') || (contextLocale === 'en' ? '12' : '24'));
    };

    window.addEventListener('hourFormatChanged', handleHourFormatChange);

    return () => {
      window.removeEventListener('hourFormatChanged', handleHourFormatChange);
    };
  }, [contextLocale]);

  // État local pour refléter les changements sans reload
  const [localEvents, setLocalEvents] = useState(events);
  
  const getNowForTimezone = (userTimezone: string) => {
    const timezone = userTimezone && moment.tz.names().includes(userTimezone) ? userTimezone : moment.tz.guess();
    return moment().tz(timezone).format('YYYY-MM-DDTHH:mm:ss');
  };

  useEffect(() => {
    setLocalEvents(events);
  }, [events]);

  const filteredEvents = useMemo(() => {
    const today = moment().startOf('day');

    if (currentView === 'listYear') {
      return localEvents.filter((event) => {
        const eventStart = moment(event.start);
        const eventEnd = event.end ? moment(event.end) : eventStart;
        return eventEnd.isSameOrAfter(today) || eventStart.isSameOrAfter(today);
      });
    }

    if (currentView === 'listWeek' && isCurrentWeek && !showFullWeek) {
      return localEvents.filter(event => moment(event.start).isSameOrAfter(today));
    }

    return localEvents;
  }, [localEvents, currentView, isCurrentWeek, showFullWeek]);


  useEffect(() => {
    if (currentView === 'listWeek' && isCurrentWeek && !isLoadingEvents && filteredEvents.length === 0) {
      setShowFullWeek(true);
    }
  }, [filteredEvents, currentView, isCurrentWeek]);
  

  //const getInitialView = () => (isMobile ? 'timeGridDay' : currentView || 'timeGridWeek');

  const getInitialView = () => {
    if (user.account_type === "Student") {
      return 'listWeek';
    }
    return isMobile ? 'timeGridDay' : currentView || 'timeGridWeek';
  };

  const handleEventDropInternal = async (info: any) => {

    const isCompleted = info.event.extendedProps.completed;
    const eventType = info.event.extendedProps.type;
    const isRestrictedType = [50, 51, 100].includes(eventType);
    const isSchoolEvent = info.event.extendedProps.invoice_type === 'S';
    const isOwned = info.event.extendedProps.isEventOwned;

    if (isRestrictedType || (user.account_type === 'S' && !isSchoolEvent && !isOwned) || (user.account_type === 'C' && isSchoolEvent)) {
      info.revert();
      return;
    }
    if (isCompleted) {
      info.revert();
      toast({
        title: t('agenda.pleaseUnlockBeforeMove'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setTooltipData(null);

      // Met à jour visuellement l’événement sans attendre un refresh
      info.event.setDates(info.event.start, info.event.end, { maintainDuration: true });

      await onEventDrop(info.event.id, info.event.start, info.event.end);

      // Met à jour aussi la copie locale
      setLocalEvents(prev =>
        prev.map(ev =>
          ev.id === info.event.id ? { ...ev, start: info.event.start, end: info.event.end } : ev
        )
      );
    } catch (err) {
      console.error(err);
      info.revert();
    }
  };

  const handleEventResizeInternal = async (info: any) => {

    const eventType = info.event.extendedProps.type;
    const isRestrictedType = [50, 51, 100].includes(eventType);
    const isSchoolEvent = info.event.extendedProps.invoice_type === 'S';


    if (isRestrictedType || (user.account_type === 'S' && !isSchoolEvent) || (user.account_type === 'C' && isSchoolEvent)) {
      info.revert();
      return;
    }
    const isCompleted = info.event.extendedProps.completed;
    if (isCompleted) {
      info.revert();
      toast({
        title: t('agenda.pleaseUnlockBeforeResize'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setTooltipData(null);

      info.event.setDates(info.event.start, info.event.end, { maintainDuration: true });

      await onEventResize(info.event.id, info.event.start, info.event.end);

      setLocalEvents(prev =>
        prev.map(ev =>
          ev.id === info.event.id ? { ...ev, start: info.event.start, end: info.event.end } : ev
        )
      );
    } catch (err) {
      console.error(err);
      info.revert();
    }
  };

  const eventContent = (eventInfo: any) => {
  const isMirror = eventInfo.event.extendedProps.isMirror || eventInfo.event.classNames?.includes("fc-mirror");
  const title = eventInfo.event.extendedProps.event_title || eventInfo.event.title || (isMirror ? "" : "");
  const timeText = eventInfo.timeText || '';
  const isCompleted = eventInfo.event.extendedProps.completed;
  const eventType = eventInfo.event.extendedProps.type;
  const isRestrictedType = [50, 51, 100].includes(eventType);
  const isSchoolEvent = eventInfo.event.extendedProps.invoice_type === 'S';
  const category = eventInfo.event.extendedProps.category || '';
  const isEventCurrent = moment().isBetween(moment(eventInfo.event.start), moment(eventInfo.event.end));
  const isEventPast = eventInfo.event.extendedProps.isEventPast === true ? true : false;
  const isAllDay = eventInfo.event.allDay;

  const eventStudents = eventInfo.event.extendedProps.students || [];
  const hasInvoice = eventStudents.length > 0 && eventInfo.event.extendedProps.invoicedEvent?.some((inv: any) => inv.student_id == eventStudents[0].id && inv.event_id == eventInfo.event.id);

  // Récupère la vue actuelle
  const calendarApi = eventInfo.view.calendar;
  const currentView = calendarApi.view.type;

  // Si la vue est "list"
  // Dans la partie "Si la vue est 'list'" de eventContent :
if (currentView.includes('list')) {
  const safeTitle = title ? title : <span className="text-gray-400 italic">{t('agenda.settingRequired')}</span>;
  const safeTimeText = timeText && timeText !== '0' ? timeText : null;

  // Formatage compact pour mobile
  const formattedTimeText = isMobile && safeTimeText
    ? safeTimeText.replace(/\s*-\s*/g, '-')  // "14:00 - 15:00" → "14:00-15:00"
    : safeTimeText;

  return (
    <div className={`fc-list-event-title-container cursor-pointer flex items-start ${isCompleted ? 'opacity-50' : ''}`}>
      {/* Contenu principal */}
      <div className="flex-1 min-w-0">
        <div className="fc-list-event-title fc-sticky text-sm">
          {safeTitle}
        </div>
        {safeTimeText && (
          <div
            className={`fc-list-event-time ${isMobile ? 'text-[10px] w-[80px] truncate' : 'text-xs w-auto'} text-gray-500 mt-1`}
            title={safeTimeText}
          >
            {formattedTimeText}
          </div>
        )}
        <div className="fc-list-event-category text-xs text-gray-500 mt-1">{category}</div>
      </div>

      {/* Badges et icône à droite */}
      <div className="flex-shrink-0 ml-2 mt-0.5 flex items-center gap-2">
        {eventInfo.event.extendedProps.invoice_type === 'S' && user.account_type === 'C' && <Building2 className="w-4 h-4 text-gray-400" />}
        {eventInfo.event.extendedProps.invoice_type === 'T' && user.account_type === 'S' && <GraduationCap className="w-4 h-4 text-gray-400" />}
        {hasInvoice && <FileText className="w-4 h-4 text-gray-400" />}
        {isCompleted ? <Lock className="w-4 h-4 text-gray-400" /> : null}
        {isEventPast && (
          <Badge key="past" variant="secondary" className="text-xs">
            {t('agenda.past')}
          </Badge>
        )}
        {isEventCurrent && !isAllDay && (
          <Badge key="current" variant="secondary" className="bg-green-50 border-green-200 text-green-500 text-xs">
            {t('agenda.inProgress')}
          </Badge>
        )}
      </div>
    </div>
  );
}






  // Logique existante pour les autres vues (timeGrid, dayGrid, etc.)
  if (isCompleted) {
    return (
      <div className="fc-event-main-frame cursor-pointer relative p-0.5 opacity-20 hover:opacity-50 transition-opacity duration-200">
        <div className="fc-event-time cursor-pointer inline-flex items-center rounded px-0.5 py-0.5 text-xs font-medium text-gray-700 hover:text-gray-900 bg-gray-50 opacity-50" style={{fontSize: '10px'}}>
          {timeText}
        </div>
        <div className="fc-event-title text-xs text-gray-700 pt-0.5">

          {user.account_type === 'C' && user.roleType === "teachers_admin" && eventInfo.event.extendedProps.event_title ? 
          <>
          <div className={`flex-1 min-h-0 mt-0.5 w-full ${isMobile ? '' : 'mb-0.5'} overflow-hidden`}>
          <div className="bg-white/70 rounded p-0.5 w-full h-full overflow-hidden flex">
            <div className="text-[11px] leading-tight text-gray-900 break-words overflow-hidden">
              {title}
            </div>
          </div>
        </div>
        </> :
            user.roleType === "teachers_all" && eventInfo.event.extendedProps.type !== 100 ?
              <>
              {!eventInfo.event.extendedProps?.isTeacherEvent && (
                <span className="flex items-center text-[11px] px-1 text-gray-900 rounded opacity-80 bg-white w-full overflow-hidden mt-0.5 mb-1">
                  <GraduationCap className="w-2 h-2 text-gray-500 mr-1" />
                  <span className="truncate min-w-0">
                    {eventInfo.event.extendedProps.teacherName}
                  </span>
                </span>
              )}
                <span className="flex items-center rounded text-[10px] px-1 text-gray-800 bg-white opacity-80 block overflow-hidden mt-0.5 mb-1 truncate">
                  {eventInfo.event.extendedProps.invoice_type === 'S' ? t('agenda.invoiceTypeS') : eventInfo.event.extendedProps.invoice_type === 'T' ? t('agenda.invoiceTypeT') : ''}
                </span>
              </> :
              user.roleType !== "teachers_all" && eventInfo.event.extendedProps.teacherName && eventInfo.event.extendedProps.type !== 100 ?
                <>
                  <span className="flex items-center text-[11px] px-1 text-gray-900 rounded opacity-80 bg-white w-full overflow-hidden mt-0.5 mb-1">
                    <GraduationCap className="w-2 h-2 text-gray-500 mr-1" />
                    <span className="truncate min-w-0">
                      {eventInfo.event.extendedProps.teacherName}
                    </span>
                  </span>
                </> : null
          }

          {(user.account_type === 'S' || user.account_type === 'Student' || user.account_type === 'Parent' || user.roleType === "teachers_all") && eventInfo.event.extendedProps.category && (
            <span className={`flex items-center text-[10px] rounded px-1 py-0.4 text-gray-800 bg-white opacity-80 block overflow-hidden ${eventInfo.event.extendedProps.type !== 100 ? 'mt-1' : ''}`}>
              <span
                className="w-2 h-2 shrink-0 rounded-full mr-1"
                style={{ backgroundColor: eventInfo.event.backgroundColor }}
              ></span>
              <span className="truncate">
                {eventInfo.event.extendedProps.category}
              </span>
            </span>
          )}
          </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 23 26" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-gray-800 bg-white rounded-full p-1 pr-1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V7.125a4.125 4.125 0 1 0-8.25 0V10.5m-1.875 0h12.75c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-12.75a1.125 1.125 0 0 1-1.125-1.125v-9.75c0-.621.504-1.125 1.125-1.125z" />
            </svg>
            {hasInvoice && (
              <FileText className="w-6 h-6 text-gray-800 bg-white rounded-full p-1" />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Rendu par défaut pour les autres vues
  return (
    <div className="fc-event-main-frame cursor-pointer relative group p-0.5" style={{ position: 'relative' }}>

      {eventInfo.event.extendedProps.type === 10 && (
      <div className="fc-event-time relative inline-flex items-center rounded bg-gray-50 px-0.5 py-0.5 text-xs font-medium text-gray-800 hover:text-xs hover:border-gray-300 transition-colors duration-200 hover:border-none hover:outline-none hover:shadow-none" style={{fontSize: '10.1px'}}>
       

        {/*((eventInfo.event.extendedProps.invoice_type === 'S' && user.account_type === 'C') || (eventInfo.event.extendedProps.invoice_type === 'T' && user.account_type === 'S')) && eventInfo.event.extendedProps.type === 100 ? (
        <div className='block overflow-hidden truncate'>
          {eventInfo.event.extendedProps.invoice_type === 'S' && user.account_type === 'C' && <Building2 className="w-2.5 h-2.5 text-gray-500" />}
          {eventInfo.event.extendedProps.invoice_type === 'T' && user.account_type === 'S' && <GraduationCap className="w-2.5 h-2.5 text-gray-500" />}
        </div>
        ) : null}
        {hasInvoice && !isCompleted && <FileText className="w-3 h-3 text-gray-500" />*/}
        {timeText}
      </div>
      )}

      <div className={(
        eventInfo.event.extendedProps.type !== 100 &&
        eventInfo.event.extendedProps.type !== 51 &&
        eventInfo.event.extendedProps.type !== 50
      ) ? (
        user.account_type === 'C' && user.roleType === "teachers_admin" ? 'pt-1 border-white/50' : 'p-0.5 border-white/50'
      ) : ''}>


      {(user.account_type !== 'C' || ((user.account_type === 'C' && user.roleType === "teachers_all") && !eventInfo.event.extendedProps?.isTeacherEvent)) &&
        eventInfo.event.extendedProps.teacherName &&
        eventInfo.event.extendedProps.teacherName.trim() !== "Coach" &&
        eventInfo.event.extendedProps.type === 10 && (
          <span className="flex items-center text-[11px] px-1 text-gray-800 rounded opacity-80 bg-white w-full overflow-hidden mt-0.5 mb-1">
            
            {/* Icône fixe à gauche */}
            {/*<GraduationCap className="w-2 h-2 text-gray-500 mr-1 flex-shrink-0" />*/}

            {/* Texte truncaté uniquement */}
            <span className="truncate min-w-0">
              {eventInfo.event.extendedProps.teacherName}
            </span>

          </span>
      )}



      {
        (
          (user.account_type === 'S') //(user.account_type === 'C' && user.roleType === "teachers_all")
          &&
          eventInfo.event.extendedProps.type === 10
        )
        ? (
          <span className="flex items-center rounded text-[11px] text-gray-800 bg-white opacity-80 overflow-hidden mb-1 mt-0.5">
            <span className="flex items-center text-[11px] px-0.5 truncate min-w-0">
            {eventInfo.event.extendedProps.invoice_type === 'S' && (
              <Building2 className="w-2.5 h-2.5 text-gray-500 mr-0.5" />
            )}
            {eventInfo.event.extendedProps.invoice_type === 'T' && (
              <GraduationCap className="w-2.5 h-2.5 text-gray-500 mr-0.5" />
            )}
            {eventInfo.event.extendedProps.invoice_type === 'S'
              ? t('agenda.invoiceTypeS')
              : eventInfo.event.extendedProps.invoice_type === 'T'
              ? t('agenda.invoiceTypeT')
              : ''}
              </span>
          </span>
        ) : null
      }



      {(user.account_type === 'S' || user.account_type === 'Student' || user.account_type === 'Parent' || user.roleType === "teachers_all") && eventInfo.event.extendedProps.category && (eventInfo.event.extendedProps.type === 10 || eventInfo.event.extendedProps.type === 100) && (
       <span
          className={`flex items-center text-[11px] px-1 text-gray-800 rounded opacity-80 bg-white block overflow-hidden ${
            eventInfo.event.extendedProps.type !== 100 ? 'mb-1' : 'mb-0'
          } ${
            eventInfo.event.extendedProps.type !== 100 ? 'mt-0.5' : 'm-0'
          }`}
        >
          <span
            className="w-2 h-2 shrink-0 rounded-full mr-1"
            style={{ backgroundColor: eventInfo.event.backgroundColor }}
          ></span>
          <span className="truncate">
            {eventInfo.event.extendedProps.category} 
          </span>
        </span>
      )}

      
          
      {(user.account_type === 'C' && eventInfo.event.extendedProps.event_title || 
        (eventInfo.event.extendedProps.type === 50 || eventInfo.event.extendedProps.type === 51)) && (
        <div className={`flex-1 min-h-0 w-full ${isMobile ? '' : ''} overflow-hidden`}>
          <div className="bg-white/50 rounded p-0.5 w-full h-full overflow-hidden flex">
            <div className="text-[11px] leading-tight text-gray-900 break-words overflow-hidden">
              {title}
            </div>
          </div>
        </div>
      )}


      {(!isRestrictedType && ((isSchoolEvent && user.account_type === 'S') || (!isSchoolEvent && user.account_type === 'C'))) && (
        <>
          <div className="absolute top-0 right-0 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-move" style={{ cursor: 'move', touchAction: 'none', zIndex: 10 }}>
            <Move className="w-3 h-3 text-gray-600" />
          </div>
          {user.account_type === 'C' && !isSchoolEvent && (
          <div
            className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:opacity-0 cursor-ns-resize"
            style={{ zIndex: 10, pointerEvents: 'none' }}
          >
            <UnfoldVertical className="w-4 h-4 text-gray-600" />
          </div>
          )}
        </>
      )}
    </div>
    </div>
  );
};


/*const handleDatesSet = (dateInfo: any) => {
    if (onDatesSet) {
      onDatesSet(dateInfo);
    }
  };*/

  const handleDatesSet = (dateInfo: any) => {

    const today = moment().startOf('day');

    const viewStart = moment(dateInfo.start);
    const viewEnd = moment(dateInfo.end).subtract(1, 'day'); // end exclusive

    const isTodayInView = today.isBetween(viewStart.subtract(1, 'day'), viewEnd.add(1, 'day'), 'day', '[]');

    setIsCurrentWeek(isTodayInView);

    // ⬇️ IMPORTANT : si on quitte la semaine courante
    if (!isTodayInView) {
      setShowFullWeek(true); // ou false selon ton UX
    }

    if (onDatesSet) {
      onDatesSet(dateInfo);
    }
  };

  // Lazy loading désactivé : on utilise un bouton "Charger plus" dans la vue listYear (voir rendu plus bas)


  /*useEffect(() => {
    const calendarApi = (calendarRef || localCalendarRef).current?.getApi();
    if (!calendarApi) return;

    if (currentView === 'timeGridWeek' || currentView === 'timeGridDay') {
      const container = document.querySelector('.fc-timegrid-body');
      const nowIndicator = container?.querySelector('.fc-timegrid-now-indicator-line');
      if (container && nowIndicator) {
        nowIndicator.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentView, calendarRef]);*/

  const now = new Date();
const currentMonth = now.getMonth(); // 0 = janvier
const monthsToShow = 12 - currentMonth;

  return (
    <div className="relative flex-1 h-full">
      <Card className="h-full border-0 rounded-none shadow-none ">
        <CardContent className="p-0 h-full ">
          {currentView === 'listWeek' && isCurrentWeek && !isLoadingEvents && localEvents.length > 0 && (
            <div className={`flex flex-wrap justify-end gap-2 pb-3 ${isMobile ? '' : 'absolute top-[-40px] right-0'}`}>
              <button
                onClick={() => setShowFullWeek(prev => !prev)}
                className="text-xs px-3 py-1 rounded-md border bg-white hover:bg-gray-50 flex items-center gap-2"
              >
                {showFullWeek
                  ? <>
                      <CalendarArrowDown className="h-4 w-4" />
                      {t('agenda.showFromToday')}
                    </>
                  : <>
                      <CalendarArrowUp className="h-4 w-4" />
                      {t('agenda.showFullWeek')}
                    </>
                }
              </button>
              <button
                onClick={handleShowUpcomingYear}
                className="text-xs px-3 py-1 rounded-md border bg-white hover:bg-gray-50 flex items-center gap-2"
              >
                <CalendarDays className="h-4 w-4" />
                {t('agenda.showUpcomingYear')}
              </button>
            </div>
          )}

          {currentView === 'listYear' && !isLoadingEvents && (
            <div className={`flex justify-end pb-3 ${isMobile ? '' : 'absolute top-[-40px] right-0'}`}>
              <button
                onClick={handleReturnToListWeek}
                className="text-xs px-3 py-1 rounded-md border bg-white hover:bg-gray-50 flex items-center gap-2"
              >
                <CalendarArrowUp className="h-4 w-4" />
                {t('agenda.showCurrentWeek')}
              </button>
            </div>
          )}
          <FullCalendar
            ref={calendarRef || localCalendarRef}
            plugins={[dayGridPlugin, multiMonthPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView={getInitialView()}
            initialDate={
              (() => {
                const savedCurrentDate = localStorage.getItem('agenda-current-date');
                const savedCheckDate = localStorage.getItem('agenda-check-date'); 
                const currentDateNow = new Date(); 

                if (savedCurrentDate && (user.account_type === "C" || user.account_type === "S")) {
                  
                  if (savedCheckDate) {
                    const parsedCheckDate = new Date(savedCheckDate);
                    
                    if (isNaN(parsedCheckDate.getTime())) {
                        return currentDateNow;
                    }

                    const timeDifferenceMs = currentDateNow.getTime() - parsedCheckDate.getTime();
                    const THRESHOLD_MS = 15 * 60 * 1000; // 30 minutes
                    if (timeDifferenceMs > THRESHOLD_MS) {
                      return currentDateNow; 
                    }
                  }
                  const parsedCurrentDate = new Date(savedCurrentDate);
                  if (isNaN(parsedCurrentDate.getTime())) {
                      return currentDateNow;
                  }
                  return parsedCurrentDate;
                }
                return currentDateNow;
              })() 
            }
            noEventsContent={() =>
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                {!isLoadingEvents && filteredEvents.length === 0 && <CalendarDays className="w-12 h-12 mb-2" />}
                <div>{isLoadingEvents ? "" : t('agenda.noEvents')}</div>
              </div>
            }
            headerToolbar={false}
            dayMaxEventRows={3}
            eventDisplay="block"
            events={filteredEvents}
            slotDuration="00:15:00"
            slotLabelInterval="00:30:00"
            snapDuration="00:05:00"
            firstDay={firstDay}
            selectMirror={true} 
            selectable={user.account_type === 'C' || user.account_type === 'S'}
            dayMaxEvents
            weekends={showWeekends}
            height="100%"
            allDayText={t('agenda.allDay')}
            locale={contextLocale}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              hour12: hourFormat === '12'
            }}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              hour12: hourFormat === '12'
            }}
            nowIndicator
            now={getNowForTimezone(user.timezone)}
            scrollTimeReset={false}
            dateClick={user.account_type === 'C' || user.account_type === 'S' ? onDateClick : undefined}
            select={user.account_type === 'C' || user.account_type === 'S' ? onSelect : undefined}
            eventClick={user.account_type === 'C' || user.account_type === 'S' || user.account_type === 'Student' || user.account_type === 'Parent'  ? onEventClick : undefined}
            datesSet={handleDatesSet}
            slotMinTime={slotMinTime}
            slotMaxTime={slotMaxTime}
            eventTextColor={eventTextColor}
            editable={user.account_type === 'C' || user.account_type === 'S'}
            eventStartEditable={user.account_type === 'C' || user.account_type === 'S'}
            eventDurationEditable={user.account_type === 'C'}
            eventAllow={(dropInfo, draggedEvent) => {
              // Interdit le déplacement vers "allDay"
              return !dropInfo.allDay;
            }}
            eventDrop={handleEventDropInternal}
            eventResize={handleEventResizeInternal}
            eventDragStart={() => setTooltipData(null)}
            eventContent={eventContent}
            eventClassNames={(arg) => {
            const createdAt = arg.event.extendedProps.createdAt;
              if (!createdAt) return [];

              const isNew = Date.now() - new Date(createdAt).getTime() < 10000;

              if (isNew) {
                //console.log('NEW EVENT', arg.event.id);
                return ['fc-new-event'];
              }

              return [];
            }}
            eventDidMount={(info) => {
              const eventType = info.event.extendedProps.type;
              const isRestrictedType = [50, 51, 100].includes(eventType);
              const isSchoolEvent = info.event.extendedProps.invoice_type === 'S';
              const isOwned = info.event.extendedProps.isEventOwned;

              if (isRestrictedType) return;

              const createdAt = info.event.extendedProps.createdAt;
              if (!createdAt) return;

              const isNew = Date.now() - new Date(createdAt).getTime() < 10000;
              if (!isNew) return;

              const scroller =
                info.el.closest('.fc-scroller') ||
                info.el.closest('.fc-scroller-liquid') ||
                info.el.closest('.fc-scroller-harness');

              if (!scroller) return;

              const scrollerRect = scroller.getBoundingClientRect();
              const rect = info.el.getBoundingClientRect();

              const pulse = document.createElement("div");
              pulse.className = "fc-pulse-overlay";

              pulse.style.top =
                (rect.top - scrollerRect.top + scroller.scrollTop) + "px";
              pulse.style.left =
                (rect.left - scrollerRect.left + scroller.scrollLeft) + "px";

              pulse.style.width = rect.width + "px";
              pulse.style.height = rect.height + "px";

              scroller.appendChild(pulse);

              setTimeout(() => pulse.remove(), 4500);
            }}
            eventMouseEnter={currentView.includes('list') ? undefined : (info) => {
              const rect = info.el.getBoundingClientRect();
              setTooltipData({
                isInProgress: moment().isBetween(moment(info.event.start), moment(info.event.end)),
                teacher: info.event.extendedProps.teacherName || '',
                isTeacherEvent: info.event.extendedProps.isTeacherEvent,
                title: info.event.extendedProps.event_title,
                category: info.event.extendedProps.category || '',
                invoice_type: info.event.extendedProps.invoice_type || '',
                type: info.event.extendedProps.type || '',
                color: info.event.backgroundColor || '#FFF',
                x: rect.left + rect.width / 2,
                y: rect.top,
              });
            }}
            eventMouseLeave={() => setTooltipData(null)}
            views={{
              listWeek: {
                listDayFormat: { weekday: 'long' },
                listDayAltFormat: { month: 'long', day: 'numeric' },                titleFormat: { year: 'numeric' },                allDayText: t('agenda.allDay')
              },
              listYear: {
                listDayFormat: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
                listDayAltFormat: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
                allDayText: t('agenda.allDay')
              },
              timeGridWeek: {
                allDaySlot: true,
                
                allDayText: t('agenda.allDayWeek')
              },

              timeGridDay: {
                allDaySlot: true,
                allDayText: t('agenda.allDayWeek'),
                dayHeaderFormat: { weekday: 'long', day: 'numeric' }
              },
              dayGridMonth: {
                allDayText: t('agenda.allDay')
              },
              multiMonthYear: {
                type: 'dayGridMonth',
                duration: { months: monthsToShow },
                dateIncrement: { years: 1 },
                titleFormat: { year: 'numeric' },
                allDayText: t('agenda.allDay'),
                multiMonthMaxColumns: 2 // 3 colonnes → grille 3x4
              },
     
            }}
          />
          {currentView === 'listYear' && canLoadMore && (
            <div className="absolute bottom-2 left-0 right-0 flex justify-center pointer-events-none z-10">
              <button
                onClick={() => onLoadMoreDates?.()}
                disabled={isLoadingEvents}
                className="pointer-events-auto text-xs px-4 py-2 rounded-md border bg-white shadow hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
              >
                {isLoadingEvents ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarArrowDown className="h-3.5 w-3.5" />}
                {t('agenda.loadMore')}
              </button>
            </div>
          )}
          {isLoadingEvents && (
            <div className="absolute top-1 right-1 text-gray-800 text-xs rounded-full flex items-center gap-1.5 shadow-sm">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            </div>
          )}
        </CardContent>
      </Card>

      {tooltipData && (
        <div
          className="fixed z-50 p-2 rounded-md bg-white shadow-lg border text-sm text-gray-800 w-80"
          style={{
            top: tooltipData.y - 10,
            left: tooltipData.x,
            transform: 'translate(-50%, -100%)'
          }}
        >
          {(user.account_type !== 'C' || (user.roleType === "teachers_all" && !tooltipData.isTeacherEvent)) && tooltipData.teacher && tooltipData.teacher.trim() !== "Coach" && (
          <div className="flex items-center text-xs text-gray-700 mb-1.5">
            <GraduationCap className="w-3 h-3 text-gray-500 mr-1" />
            <span>{tooltipData.teacher}</span>
          </div>
          )}
          <div className="flex items-center gap-1">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: tooltipData.color }}
            ></span>
            <span>{tooltipData.category}</span>
            {/* Ajout du point vert clignotant et du texte "In Progress" */}
            {tooltipData.isInProgress && (
              <div className="flex items-center gap-1 ml-auto">
                <span className="absolute top-2 right-2 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
               {/*<span className="text-xs text-green-600 font-medium">
                  {t('agenda.inProgress')}
                </span>*/}
              </div>
            )}
          </div>

          {user.account_type === 'C' && user.roleType === "teachers_all" && (tooltipData.type != 50 && tooltipData.type != 51) && (
            <span className="flex items-center text-xs text-gray-700 mt-2">
              <FileText className="w-3 h-3 text-gray-500 mr-1" />
              {tooltipData.invoice_type === 'S' ? t('agenda.invoiceTypeS') : tooltipData.invoice_type === 'T' ? t('agenda.invoiceTypeT') : ''}
            </span>
          )}

          {tooltipData.title && (
          <div className="text-xs p-1.5 bg-gray-50 px-1 mt-2 rounded">
            <Users width={12} height={12} className="inline-block mr-1 text-gray-500" />
            {tooltipData.title}
            </div>
          )}



        </div>
      )}
    </div>
  );
};

export default CalendarView;